import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation, useOutletContext } from "react-router-dom";
import sessionStorage from "./utils/sessionStore";
import { Spinner, Alert, Form, Button, Container, Row, Col } from "react-bootstrap";
import "./pagecss/Webappcss.css";
import { NestedDataGrid, buildColumnTree } from "./components/NestedDataGrid";
import SearchableSelect from "./components/SearchableSelect";
import { withRefDataPreload } from "./utils/refDataConfigs";
import { peek as peekRefData } from "./utils/refDataCache";
import AddressAutocompleteField from "./components/AddressAutocompleteField";
import { findMenuBySlug, getLeafSlug } from "./utils/menuSlug";
import { hasAction, canAccessMenu, isAdminOrDistrRole } from "./utils/auth";
import {
  validateAddress as validateAddressApi,
  peekValidation,
  rememberValidation,
  correctedFrom,
  isSafeToAdopt,
} from "./utils/addressValidation";
import "./pagecss/DynamicQw.css";

const API_URL = process.env.REACT_APP_API_URL;

// ---------------------------------------------------------------------------
// Role helpers
// ---------------------------------------------------------------------------
function isAdminOrDistributor() {
  return isAdminOrDistrRole(sessionStorage.getItem("userRole"));
}
// True only when the caller is an admin/distributor AND their JWT territory
// claim is D99. Mirrors the server-side rule in TerritoryController's
// IsD99AdminOrDistributor — used here to decide which form fields can be
// freely edited vs. locked to the caller's own territory.
function isD99AdminOrDistributor() {
  if (!isAdminOrDistributor()) return false;
  return (sessionStorage.getItem("territoryId") || "").toUpperCase() === "D99";
}
// True for a non-D99 admin/distributor whose territory is a sub-distributor
// (e.g. "D57-SD01") — i.e. its parent is another territory, not D99.
// Distributor-level territories ("D57") have no hyphen and their parent is
// D99. Used on menu 2065 to decide whether the rep's Territory ID is merely
// defaulted (distributor — can still pick one of their sub-territories) or
// fully locked (sub-distributor — can only create reps in their own territory).
function isSubTerritoryAdminOrDistributor() {
  if (!isAdminOrDistributor() || isD99AdminOrDistributor()) return false;
  return (sessionStorage.getItem("territoryId") || "").includes("-");
}
// AddressAutocompleteField is the shared component in
// ./components/AddressAutocompleteField (imported above). It defaults to a
// `form-control` input, so the DB-driven usage below is unchanged.

/**
 * Resolves a data path like "territories[0].salesTRPs" against a JSON object.
 * Supports simple access ("territories"), array indexing ("territories[0]"),
 * and chained paths ("territories[0].salesTRPs").
 */
function resolveDataPath(obj, path) {
  if (!obj || !path) return undefined;
  const tokens = path.replace(/\[(\d+)\]/g, ".$1").split(".");
  let current = obj;
  for (const token of tokens) {
    if (current == null) return undefined;
    if (/^\d+$/.test(token)) {
      current = Array.isArray(current) ? current[parseInt(token, 10)] : undefined;
    } else {
      current = current[token];
    }
  }
  return current;
}

// Evaluate a JSON condition against row/editData
// e.g. {"field": "parentTerritory_c", "operator": "eq", "value": "D99"}
function evaluateCondition(conditionJson, data) {
  if (!conditionJson || !data) return true; // no condition = always show
  try {
    const cond = typeof conditionJson === "string" ? JSON.parse(conditionJson) : conditionJson;
    const fieldVal = (() => {
      // Case-insensitive field lookup
      const key = Object.keys(data).find(
        (k) => k.toLowerCase() === cond.field?.toLowerCase()
      );
      return key ? String(data[key] ?? "") : "";
    })();
    const condVal = String(cond.value ?? "");

    switch (cond.operator) {
      case "eq":       return fieldVal.toLowerCase() === condVal.toLowerCase();
      case "neq":      return fieldVal.toLowerCase() !== condVal.toLowerCase();
      case "contains": return fieldVal.toLowerCase().includes(condVal.toLowerCase());
      case "in":       return Array.isArray(cond.value) && cond.value.map(v => String(v).toLowerCase()).includes(fieldVal.toLowerCase());
      default:         return true;
    }
  } catch {
    return true; // parse error = show button
  }
}

// Parse an ASP.NET ModelState `errors` map (or anything close — handles the
// outer ProblemDetails wrapper too) into our per-field error shape keyed by
// lowercase API parameter name. Returns {} if the body has no errors map.
function parseFieldErrors(body) {
  if (!body || typeof body !== "object") return {};
  const errors = body.errors || (body.title && body.errors) || null;
  if (!errors || typeof errors !== "object") return {};
  const result = {};
  for (const [k, v] of Object.entries(errors)) {
    if (!k) continue;
    const msg = Array.isArray(v) ? v.filter(Boolean).join(" ") : String(v ?? "");
    if (msg) result[k.toLowerCase()] = msg;
  }
  return result;
}

// Look up a server error for a form field. The validation API uses keys like
// "ZipCode" / "City" while the form's apiParamName might be "Zip" / "City" —
// try the exact key, then a "Code" suffix on either side, before giving up.
function fieldErrorFor(apiParamName, fieldErrors) {
  if (!apiParamName || !fieldErrors) return null;
  const key = apiParamName.toLowerCase();
  return fieldErrors[key]
      || fieldErrors[key + "code"]
      || fieldErrors[key.replace(/code$/, "")]
      || null;
}

// Map a dropdown's display option to its API value via ValueMappings
// (e.g. "Sales Rep" → "Rep", "Customer Service" → "CustomerService").
// Falls back to the display value when no mapping applies.
function dropdownApiValue(field, displayValue) {
  if (!field?.valueMappings || !displayValue) return displayValue;
  try {
    const mappings = typeof field.valueMappings === "string"
      ? JSON.parse(field.valueMappings)
      : field.valueMappings;
    return mappings[displayValue] !== undefined ? String(mappings[displayValue]) : displayValue;
  } catch {
    return displayValue;
  }
}

function DynamicForm() {
  // Route is /:moduleSlug/create or /:moduleSlug/edit (or .../edit/:recordId).
  // `moduleSlug` is either a numeric menuId (legacy URLs) or a slug. The
  // backend's by-slug endpoint resolves whichever we send it. For internal
  // logic that compares the numeric id (hardcoded "2064" / "2065" checks),
  // we still need the numeric — resolve via the menu tree the navbar shares
  // through outlet context.
  const { moduleSlug } = useParams();
  const { menuData = [] } = useOutletContext() || {};
  const isNumeric = /^\d+$/.test(moduleSlug || '');
  const menuId = isNumeric
    ? moduleSlug
    : (findMenuBySlug(menuData, moduleSlug)?.menuId?.toString() || null);
  // Path segment to use when reconstructing URLs back to /:slug/search —
  // preserves whichever form the user arrived in.
  const routeSegment = moduleSlug;
  const navigate = useNavigate();
  const location = useLocation();

  // Edit mode: editData passed from search page via route state
  const editData = location.state?.editData || null;
  // Optional return target for the back button. When this form was opened
  // from a parent menu's child grid (e.g. editing a Sales Rep from inside
  // the Sales Territory edit screen), `returnTo` carries the *full* origin
  // location — pathname + state — so the user lands back on the parent's
  // edit page with its editData intact, not just on the parent's search.
  // Shape: { pathname: string, state?: object } | string (legacy path-only).
  const returnTo = location.state?.returnTo || null;
  const isEditMode = !!editData;
  // Territory (2064) and Sales Rep (2065) records are view-only unless the
  // login's action entitlements (JWT "actions" claim, RoleActions table)
  // grant edit — 00003 (edit territory) / 00006 (edit sales rep). Sessions
  // from before entitlements shipped fall back to the old role check until
  // re-login. View-only users can open a record but not create/edit/update.
  // Search visibility is scoped separately, server-side
  // (ApplyTerritoryScope / ResolveTerritoryScope).
  const isViewOnlyForRole =
    (menuId === "2064" && !hasAction("00003", isAdminOrDistributor())) ||
    (menuId === "2065" &&
      !(hasAction("00013", isAdminOrDistributor()) || hasAction("00006", false)));

  // Child-grid entitlements on the Sales Territory form (menu 2064): the
  // Sales Representatives grid (addMenuId 2065) needs 00005 to add and 00006
  // to edit rows; the Sub-Territories grid (addMenuId null → 2064) needs
  // 00009 to add and 00003 to edit. Other menus keep the plain
  // admin/distributor gate, which is also the fallback for pre-entitlement
  // sessions.
  const canAddToChildGrid = (grid) => {
    if (menuId !== "2064") return isAdminOrDistributor();
    return String(grid.addMenuId) === "2065"
      ? hasAction("00005", isAdminOrDistributor())
      : hasAction("00009", isAdminOrDistributor());
  };
  const canEditChildGridRows = (grid) => {
    if (menuId !== "2064") return isAdminOrDistributor();
    return String(grid.addMenuId) === "2065"
      ? hasAction("00006", isAdminOrDistributor())
      : hasAction("00003", isAdminOrDistributor());
  };
  // The only editable-in-grid column on the territory form is the rep
  // Primary/Alt toggle — action 00007.
  const childGridReadOnly =
    isViewOnlyForRole ||
    (menuId === "2064" && !hasAction("00007", isAdminOrDistributor()));

  // Defaults passed from child grid "Add" button (e.g. { ParentTerritory_c: "D20" })
  const childGridDefaults = location.state?.childGridDefaults || null;

  const [fields, setFields] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [submitErrorMessage, setSubmitErrorMessage] = useState(null);
  const [addressValidationError, setAddressValidationError] = useState(null);
  // Per-field server-side validation errors keyed by lowercase apiParamName.
  // Populated from ASP.NET ModelState `errors` maps returned by validate /
  // save endpoints; rendered as Bootstrap invalid-feedback under each input.
  const [fieldErrors, setFieldErrors] = useState({});
  const [suggestedAddress, setSuggestedAddress] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validated, setValidated] = useState(false);
  const [formTitle, setFormTitle] = useState("");
  const [formSubmitApiUrl, setFormSubmitApiUrl] = useState(null);
  const [formUpdateApiUrl, setFormUpdateApiUrl] = useState(null);
  const [formDefaultValues, setFormDefaultValues] = useState(null);
  const [hasSearchPage, setHasSearchPage] = useState(false);

  // ---- Child grids state (edit mode only) ----
  const [childGrids, setChildGrids] = useState([]);
  const [childGridData, setChildGridData] = useState({});
  const [collapsedGrids, setCollapsedGrids] = useState({});

  // ---- Zip → City/State auto-lookup debounce ----
  // The Zip text/autocomplete field fans out to City/State (and Country,
  // when present) after a brief idle so each keystroke past the 5-digit
  // threshold doesn't hammer the geocoding endpoint.
  const zipLookupDebounceRef = useRef(null);

  // ---- Menu 2065: Customer Service role gating ----
  // The Role dropdown only offers "Customer Service" when the selected
  // territory is a DIRECT child of D99 (HQ). We track the selected
  // territory's parent here: resolved synchronously from the preloaded
  // territory cache when possible, else via a targeted /api/Territory/info
  // lookup. null = unknown/none (option hidden). Mirrored server-side in
  // SalesRepController.CreateFullSalesRep so the rule isn't UI-only.
  const [repTerritoryParent, setRepTerritoryParent] = useState(null);
  const territoryFieldId = menuId === "2065"
    ? fields.find((f) => f.apiParamName === "TerritoryID")?.fieldId
    : undefined;
  const territoryValue = territoryFieldId != null ? formValues[territoryFieldId] : "";

  useEffect(() => {
    if (menuId !== "2065") return;
    const id = String(territoryValue || "").trim().toUpperCase();
    if (!id) { setRepTerritoryParent(null); return; }

    const cached = peekRefData("territories");
    const hit = Array.isArray(cached)
      ? cached.find((t) => String(t.territoryID || "").toUpperCase() === id)
      : null;
    if (hit) {
      setRepTerritoryParent(String(hit.parentTerritory_c || "").toUpperCase() || null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(
          `${API_URL}/api/Territory/info?TerritoryID=${encodeURIComponent(id)}&Top=1`,
          { cache: "no-store" }
        );
        if (!resp.ok) throw new Error(`status ${resp.status}`);
        const data = await resp.json();
        const t = (data?.territories || [])[0];
        if (!cancelled) {
          setRepTerritoryParent(
            t ? (String(t.parentTerritory_c || "").toUpperCase() || null) : null
          );
        }
      } catch {
        if (!cancelled) setRepTerritoryParent(null);
      }
    })();
    return () => { cancelled = true; };
  }, [menuId, territoryValue]);

  // If the user picked a territory-conditional role (Customer Service /
  // IT) and then switched to a territory that no longer qualifies,
  // drop the now-invalid selection (create mode only — edit mode keeps
  // pre-existing values visible).
  useEffect(() => {
    if (menuId !== "2065" || isEditMode) return;
    const roleField = fields.find((f) => f.apiParamName === "RoleCode");
    if (!roleField) return;
    const current = formValues[roleField.fieldId];
    if (!current) return;
    const api = dropdownApiValue(roleField, current);
    const terr = String(territoryValue || "").trim().toUpperCase();
    const invalid =
      (api === "CustomerService" && repTerritoryParent !== "D99")
      || (api === "IT" && terr !== "D99" && terr !== "");
    if (invalid) {
      setFormValues((prev) => ({ ...prev, [roleField.fieldId]: "" }));
    }
    // formValues is intentionally not a dep — this only needs to re-run when
    // the eligibility flips, not on every keystroke elsewhere in the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuId, isEditMode, repTerritoryParent, territoryValue, fields]);

  // Load form field configuration
  useEffect(() => {
    if (!menuId) return;

    const fetchFields = async () => {
      setIsLoading(true);
      setError(null);
      setSubmitStatus(null);
      setSubmitErrorMessage(null);
      setFieldErrors({});

      try {
        const response = await fetch(`${API_URL}/api/Common/DynamicFormFields/${menuId}`, { cache: 'no-store' });

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        const data = await response.json();
        // Only show input/entry fields, not search fields
        const inputFields = data.filter((f) => f.formMode !== "search");
        const searchFields = data.filter((f) => f.formMode === "search");
        setFields(inputFields);
        setHasSearchPage(searchFields.length > 0);

        // Initialize form values (populate from editData if in edit mode)
        const initialValues = {};
        inputFields.forEach((f) => {
          if (isEditMode && f.apiParamName) {
            // Try direct match first, then suffix match (e.g., "Territory_TerritoryID" → "TerritoryID")
            let val = editData[f.apiParamName];
            if (val === undefined) {
              const suffixKey = Object.keys(editData).find(
                (k) => k.toLowerCase().endsWith(`_${f.apiParamName.toLowerCase()}`)
                     || k.toLowerCase() === f.apiParamName.toLowerCase()
              );
              if (suffixKey) val = editData[suffixKey];
            }

            if (val !== undefined && val !== null) {
              if (f.fieldType === "checkbox") {
                initialValues[f.fieldId] = val === true || val === "true" || val === "True";
              } else if (f.fieldType === "dropdown" && f.valueMappings) {
                // Reverse-map API value → display option (e.g. "Rep" → "Sales Rep")
                try {
                  const mappings = typeof f.valueMappings === "string"
                    ? JSON.parse(f.valueMappings) : f.valueMappings;
                  const displayKey = Object.keys(mappings).find(
                    (k) => String(mappings[k]).toLowerCase() === String(val).toLowerCase()
                  );
                  initialValues[f.fieldId] = displayKey || String(val);
                } catch { initialValues[f.fieldId] = String(val); }
              } else if (f.fieldType === "date") {
                // <input type="date"> only accepts YYYY-MM-DD. The API serialises
                // DateTime as "2026-05-12T00:00:00"; trim the time component or
                // the input renders empty.
                const s = String(val);
                initialValues[f.fieldId] = s.length >= 10 ? s.substring(0, 10) : s;
              } else {
                initialValues[f.fieldId] = String(val);
              }
            } else {
              initialValues[f.fieldId] = f.fieldType === "checkbox" ? false : "";
            }
          } else {
            // Check if childGridDefaults has a value for this field's apiParamName
            if (childGridDefaults && f.apiParamName && childGridDefaults[f.apiParamName] !== undefined) {
              initialValues[f.fieldId] = String(childGridDefaults[f.apiParamName]);
            } else if (
              menuId === "2064"
              && f.apiParamName === "ParentTerritory_c"
              && isAdminOrDistributor()
              && !isD99AdminOrDistributor()
            ) {
              // Non-D99 admin/distributor: parent territory locks to the
              // caller's own territory — they can't reparent under D99 or
              // a sibling subtree. The field is also disabled below.
              initialValues[f.fieldId] = (sessionStorage.getItem("territoryId") || "").toUpperCase();
            } else if (
              menuId === "2065"
              && f.apiParamName === "TerritoryID"
              && isAdminOrDistributor()
              && !isD99AdminOrDistributor()
            ) {
              // Non-D99 admin/distributor creating a rep: the Territory ID is
              // pre-filled with the caller's own territory. A distributor-level
              // user can still change it (the /api/Territory/info autocomplete
              // is scoped to their subtree server-side); a sub-territory user's
              // field is also disabled below.
              initialValues[f.fieldId] = (sessionStorage.getItem("territoryId") || "").toUpperCase();
            } else if (f.defaultValue !== undefined && f.defaultValue !== null && f.defaultValue !== "") {
              // Apply DB default value
              initialValues[f.fieldId] = f.fieldType === "checkbox"
                ? (f.defaultValue === "true" || f.defaultValue === "True")
                : String(f.defaultValue);
            } else {
              initialValues[f.fieldId] = f.fieldType === "checkbox" ? false : "";
            }
          }
        });
        setFormValues(initialValues);
        setValidated(false);

        // ── Enrich editData: if opened from a child grid, editData may be
        //    incomplete (e.g. SalesTRPs row has no email). Fetch the full
        //    record via formUpdateApiUrl and backfill any empty fields. ──
        if (isEditMode) {
          try {
            const cfgResp = await fetch(`${API_URL}/api/Common/SearchConfig/${menuId}`, { cache: 'no-store' });
            if (cfgResp.ok) {
              const cfg = await cfgResp.json();
              if (cfg?.formUpdateApiUrl) {
                // Resolve {placeholders} from editData
                const resolvedUrl = cfg.formUpdateApiUrl.replace(/\{(\w+)\}/g, (match, key) => {
                  if (editData[key] !== undefined) return encodeURIComponent(editData[key]);
                  const found = Object.keys(editData).find(
                    (k) => k.toLowerCase() === key.toLowerCase()
                  );
                  return found ? encodeURIComponent(editData[found]) : match;
                });

                // Only fetch if the URL has no unresolved placeholders
                if (!resolvedUrl.includes("{")) {
                  const hdrs = {};
                  const tk = sessionStorage.getItem("authToken");
                  if (tk) hdrs["Authorization"] = `Bearer ${tk}`;

                  const fullResp = await fetch(`${API_URL}${resolvedUrl}`, { headers: hdrs });
                  if (fullResp.ok) {
                    let fullData = await fullResp.json();
                    // Unwrap API envelope — if the response has a single
                    // object property (e.g. { success, salesRep: {...} }),
                    // use that inner object so field lookups work correctly.
                    const dataKeys = Object.keys(fullData).filter(
                      (k) => k !== "success" && typeof fullData[k] === "object" && fullData[k] !== null && !Array.isArray(fullData[k])
                    );
                    if (dataKeys.length === 1) {
                      fullData = fullData[dataKeys[0]];
                    }
                    // Backfill any form fields that are still empty
                    const updatedValues = { ...initialValues };
                    let changed = false;
                    inputFields.forEach((f) => {
                      if (!f.apiParamName) return;
                      if (updatedValues[f.fieldId] !== undefined && updatedValues[f.fieldId] !== "") return;

                      let val = fullData[f.apiParamName];
                      if (val === undefined) {
                        const fk = Object.keys(fullData).find(
                          (k) => k.toLowerCase() === f.apiParamName.toLowerCase()
                        );
                        if (fk) val = fullData[fk];
                      }
                      if (val !== undefined && val !== null && val !== "") {
                        if (f.fieldType === "checkbox") {
                          updatedValues[f.fieldId] = val === true || val === "true";
                        } else if (f.fieldType === "dropdown" && f.valueMappings) {
                          try {
                            const mappings = typeof f.valueMappings === "string"
                              ? JSON.parse(f.valueMappings) : f.valueMappings;
                            const displayKey = Object.keys(mappings).find(
                              (k) => String(mappings[k]).toLowerCase() === String(val).toLowerCase()
                            );
                            updatedValues[f.fieldId] = displayKey || String(val);
                          } catch { updatedValues[f.fieldId] = String(val); }
                        } else {
                          updatedValues[f.fieldId] = String(val);
                        }
                        changed = true;
                      }
                    });
                    if (changed) setFormValues(updatedValues);
                  }
                }
              }
            }
          } catch (enrichErr) {
            console.warn("Could not enrich edit data:", enrichErr);
          }
        }
      } catch (err) {
        console.error("Error fetching form fields:", err);
        setError(`Failed to load form: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFields();

    // Fetch menu config (title, submit API URL, default values)
    fetch(`${API_URL}/api/Common/SearchConfig/${menuId}`, { cache: 'no-store' })
      .then((r) => r.ok ? r.json() : null)
      .then((config) => {
        // Set unconditionally. This DynamicForm component instance is reused
        // when navigating between menus (e.g. a child grid's "Add" button
        // → /dynamic-form/{addMenuId}), so config from the previous menu must
        // be REPLACED, not left stale — otherwise a form can submit to the
        // wrong endpoint.
        setFormTitle(config?.menuTitle || "");
        setFormSubmitApiUrl(config?.formSubmitApiUrl || null);
        setFormUpdateApiUrl(config?.formUpdateApiUrl || null);
        let parsedDefaults = null;
        if (config?.formDefaultValues) {
          try {
            parsedDefaults = typeof config.formDefaultValues === "string"
              ? JSON.parse(config.formDefaultValues)
              : config.formDefaultValues;
          } catch { /* ignore parse errors */ }
        }
        setFormDefaultValues(parsedDefaults);
      })
      .catch(() => {});
    // childGridDefaults / editData are read once on mount via location.state;
    // re-running this effect when they change would refetch fields and stomp
    // user input. menuId + location.key cover the intended re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuId, location.key]);

  // Load child grid configs when in edit mode.
  // Server-driven pagination: each grid tracks { page, pageSize, totalCount }
  // and re-fetches a single page on Prev/Next. totalCount comes from the
  // backend (see TerritoryListResponse.TotalCount); when it's absent or
  // <= the current page size we hide the pagination bar so grids backed by
  // nested-array endpoints (e.g. /api/Territory → territories[0].salesTRPs)
  // continue to render without spurious paginators.
  const CHILD_GRID_PAGE_SIZE = 10;

  // Fetch a single page for a given child grid. Resolves {placeholders} in
  // the URL template, then forces Top/Skip via URLSearchParams so any legacy
  // Top= baked into the template (e.g. older &Top=500 hacks) is overridden
  // cleanly.
  const fetchChildGridPage = async (grid, page) => {
    setChildGridData((prev) => ({
      ...prev,
      [grid.childGridId]: {
        ...(prev[grid.childGridId] || {}),
        loading: true,
        error: null,
      },
    }));

    try {
      const resolvedTemplate = grid.gridApiUrl.replace(/\{(\w+)\}/g, (match, key) => {
        if (editData[key] !== undefined) return encodeURIComponent(editData[key]);
        const found = Object.keys(editData).find(
          (k) => k.toLowerCase() === key.toLowerCase()
        );
        return found ? encodeURIComponent(editData[found]) : match;
      });

      const url = new URL(`${API_URL}${resolvedTemplate}`);
      url.searchParams.set("Top", String(CHILD_GRID_PAGE_SIZE));
      url.searchParams.set("Skip", String(page * CHILD_GRID_PAGE_SIZE));

      const childHeaders = {};
      const childToken = sessionStorage.getItem("authToken");
      if (childToken) childHeaders["Authorization"] = `Bearer ${childToken}`;

      const resp = await fetch(url.toString(), { headers: childHeaders });
      if (!resp.ok) throw new Error(`API error: ${resp.status}`);
      const json = await resp.json();

      const gridData = resolveDataPath(json, grid.gridDataPath) || [];
      // Nested data paths (e.g. "territories[0].salesTRPs") describe the
      // grid's data INSIDE an outer envelope, so the envelope's totalCount
      // refers to the outer collection — not the nested array. Trusting it
      // there shows misleading headers like "(1 record)" when the nested
      // array is actually empty. Use the rendered length instead.
      const isNestedPath = /[.[]/.test(grid.gridDataPath || "");
      const total = isNestedPath
        ? (Array.isArray(gridData) ? gridData.length : 0)
        : (json?.totalCount ?? json?.count ?? gridData.length ?? 0);

      setChildGridData((prev) => ({
        ...prev,
        [grid.childGridId]: {
          data: Array.isArray(gridData) ? gridData : [],
          loading: false,
          error: null,
          page,
          pageSize: CHILD_GRID_PAGE_SIZE,
          totalCount: total,
        },
      }));
    } catch (err) {
      setChildGridData((prev) => ({
        ...prev,
        [grid.childGridId]: {
          ...(prev[grid.childGridId] || {}),
          data: [],
          loading: false,
          error: err.message,
        },
      }));
    }
  };

  useEffect(() => {
    // Always reset child grids on navigation/mode change
    setChildGrids([]);
    setChildGridData({});

    if (!menuId || !isEditMode) return;

    const fetchChildGrids = async () => {
      try {
        const resp = await fetch(`${API_URL}/api/Common/DynamicFormChildGrids/${menuId}`, { cache: 'no-store' });
        if (!resp.ok) return;
        const allGrids = await resp.json();
        if (!allGrids || allGrids.length === 0) return;

        // Drop grids whose VisibilityCondition evaluates false against the
        // current editData (e.g. Sub-Territories grid is hidden when the
        // territory being edited isn't a child of D99). Skips the fetch too.
        const grids = allGrids.filter((g) => evaluateCondition(g.visibilityCondition, editData));
        if (grids.length === 0) return;

        setChildGrids(grids);

        const initialState = {};
        grids.forEach((g) => {
          initialState[g.childGridId] = {
            data: [],
            loading: true,
            error: null,
            page: 0,
            pageSize: CHILD_GRID_PAGE_SIZE,
            totalCount: 0,
          };
        });
        setChildGridData(initialState);

        // Fetch the first page of each grid in parallel
        grids.forEach((grid) => fetchChildGridPage(grid, 0));
      } catch (err) {
        console.error("Error fetching child grid configs:", err);
      }
    };

    fetchChildGrids();
    // editData is read once via location.state; menuId + isEditMode +
    // location.key cover all intended re-fetches, and including editData
    // would loop on every form value change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuId, isEditMode, location.key]);

  // Handle field value change
  const handleChange = (fieldId, value) => {
    setFormValues((prev) => ({ ...prev, [fieldId]: value }));
    // Clear any server-side error on this field as soon as the user edits it.
    const field = fields.find((f) => f.fieldId === fieldId);
    if (field?.apiParamName && Object.keys(fieldErrors).length > 0) {
      const key = field.apiParamName.toLowerCase();
      if (fieldErrors[key] || fieldErrors[key + "code"] || fieldErrors[key.replace(/code$/, "")]) {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[key];
          delete next[key + "code"];
          delete next[key.replace(/code$/, "")];
          return next;
        });
      }
    }

    // Zip → City/State auto-lookup. Trigger only when the changed field is
    // the Zip field and its value matches a US 5-digit ZIP. Debounced so the
    // user can finish typing before the geocoding call fires. This is a
    // fallback for plain-text Zip fields and for autocomplete cases where
    // the user types a full zip that has no existing-rep match — the
    // autocomplete dropdown won't fire onSelectItem, but we still want
    // City/State to populate.
    if (field && (field.apiParamName === "Zip" || field.apiParamName === "ZipCode")) {
      if (zipLookupDebounceRef.current) clearTimeout(zipLookupDebounceRef.current);
      const trimmed = (value || "").trim();
      if (/^\d{5}$/.test(trimmed)) {
        zipLookupDebounceRef.current = setTimeout(() => {
          lookupAddressByZip(trimmed);
        }, 400);
      }
    }
  };

  // Fetch city/state for a ZIP code and merge into matching form fields.
  // Fields are matched by ApiParamName so this works for any menu that has
  // standard address fields configured (City/State/Country/CountryNum), not
  // just the Sales Rep form. Address1 is intentionally NOT overwritten —
  // the user supplies the street themselves.
  const lookupAddressByZip = async (zip) => {
    try {
      const resp = await fetch(`${API_URL}/api/AddressAutocomplete/byZip/${encodeURIComponent(zip)}?country=US`);
      if (!resp.ok) return;
      const data = await resp.json();
      const paramMap = {
        City: data.city,
        State: data.state,
        Zip: data.zipCode || zip,
        ZipCode: data.zipCode || zip,
        Country: data.country,
        CountryNum: data.countryNum,
      };
      setFormValues((prev) => {
        const next = { ...prev };
        fields.forEach((f) => {
          if (!f.apiParamName) return;
          const v = paramMap[f.apiParamName];
          if (v === undefined || v === null || v === "") return;
          // Always overwrite: when the user enters or changes the Zip, the
          // matching City/State/Country are part of that ZIP — anything
          // pre-existing was either typed before the zip lookup ran or
          // belongs to a stale prior selection. Address1 is intentionally
          // omitted from paramMap so the street stays user-controlled.
          next[f.fieldId] = String(v);
        });
        return next;
      });
    } catch (err) {
      console.warn("Zip lookup failed:", err);
    }
  };

  // Apply the corrected address returned by the validation API into form fields
  const applySuggestedAddress = () => {
    if (!suggestedAddress) return;
    const paramToValue = {
      Address1: suggestedAddress.address1,
      City: suggestedAddress.city,
      State: suggestedAddress.state,
      Zip: suggestedAddress.zipCode,
      ZipCode: suggestedAddress.zipCode,
    };
    setFormValues((prev) => {
      const updated = { ...prev };
      fields.forEach((f) => {
        if (f.apiParamName && paramToValue[f.apiParamName] !== undefined) {
          updated[f.fieldId] = paramToValue[f.apiParamName];
        }
      });
      return updated;
    });
    setAddressValidationError(null);
    setSuggestedAddress(null);
  };

  // Submit form: save audit trail locally + POST directly to entity API
  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    // Single-level sub-territory depth guard (menu 2064). Block creating a
    // territory whose parent is itself a sub-territory (parent ID contains "-").
    // Backstops the autocomplete clientFilter for D99 admins, and catches any
    // form path that pre-fills a hyphenated parent for non-D99 users too.
    if (menuId === "2064" && !isEditMode) {
      const parentField = fields.find((f) => f.apiParamName === "ParentTerritory_c");
      const parentValue = parentField ? String(formValues[parentField.fieldId] || "") : "";
      if (parentValue && parentValue.includes("-")) {
        setSubmitStatus("error");
        setSubmitErrorMessage(
          `Cannot create a sub-territory under "${parentValue}". Only one level of sub-territory is allowed — pick a top-level territory (e.g. D99 or a distributor like D01, D57) as the parent.`
        );
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitErrorMessage(null);
    setAddressValidationError(null);
    setSuggestedAddress(null);
    setFieldErrors({});

    try {
      // Build JSON body from field values keyed by ApiParamName
      const body = {};

      // In edit mode, start with all original row data so IDs and
      // non-form fields are preserved in the request
      if (isEditMode && editData) {
        Object.assign(body, editData);
      }

      fields.forEach((f) => {
        if (!f.apiParamName) return;
        let raw = formValues[f.fieldId];

        // Apply valueMappings for dropdowns (e.g., "Sales Rep" → "Rep")
        if (f.fieldType === "dropdown" && f.valueMappings && raw) {
          try {
            const mappings = typeof f.valueMappings === "string"
              ? JSON.parse(f.valueMappings)
              : f.valueMappings;
            if (mappings[raw] !== undefined) raw = mappings[raw];
          } catch { /* ignore parse errors */ }
        }

        if (f.fieldType === "checkbox") {
          body[f.apiParamName] = !!raw;
        } else if (f.fieldType === "number") {
          // Send 0 instead of empty string for number fields (non-nullable decimals)
          body[f.apiParamName] = (raw !== "" && raw != null) ? Number(raw) : 0;
        } else if (f.fieldType === "date") {
          // Send null instead of empty string for date fields
          body[f.apiParamName] = (raw !== "" && raw != null) ? raw : null;
        } else {
          body[f.apiParamName] = String(raw ?? "");
        }
      });

      // Merge default values (only if not already set by user)
      if (formDefaultValues) {
        Object.entries(formDefaultValues).forEach(([key, val]) => {
          if (!(key in body)) {
            body[key] = val;
          }
        });
      }

      // ── Address Validation ──
      const addr1 = body["Address1"] || "";
      const city = body["City"] || "";
      const state = body["State"] || "";
      const zip = body["Zip"] || body["ZipCode"] || "";
      const hasAddress = addr1 || city || state || zip;

      if (hasAddress) {
        const addrInput = {
          address1: addr1,
          address2: body["Address2"] || "",
          city: city,
          state: state,
          zipCode: zip,
        };

        // When the address came from autocomplete it was already validated and
        // standardized on pick, so this is normally a cache hit and costs no
        // second billed call. The fetch still runs for hand-typed addresses —
        // and it stays a raw fetch here because the non-OK branch below needs
        // the response body to route ModelState errors to individual fields.
        let valResult = peekValidation(addrInput);
        let valResponse = null;
        if (!valResult) {
          valResponse = await fetch(`${API_URL}/api/AddressValidation/validate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...addrInput, country: "US" }),
          });
          if (valResponse.ok) {
            valResult = await valResponse.json();
            rememberValidation(addrInput, valResult);
          }
        }

        if (valResult) {

          if (!valResult.isValid) {
            setAddressValidationError(
              valResult.errorMessage || "The address entered is not valid. Please correct it and try again."
            );
            setIsSubmitting(false);
            return;
          }

          // Check if API has unconfirmed components or corrected any field
          const corrected = correctedFrom(valResult);
          const addressChanged =
            corrected.address1.toUpperCase() !== addr1.toUpperCase() ||
            corrected.city.toUpperCase() !== city.toUpperCase() ||
            corrected.state.toUpperCase() !== state.toUpperCase() ||
            corrected.zipCode !== zip;

          // Google flags `hasUnconfirmedComponents` even when it didn't
          // actually correct anything — e.g. a suite number with no street
          // is "unconfirmed" but the suggested address is just the input
          // uppercased. In that case the warning is pure friction, so let
          // the submit proceed. We only block when Google genuinely
          // changed something AND can't fully confirm the result.
          if (valResult.hasUnconfirmedComponents && addressChanged) {
            setSuggestedAddress(corrected);
            setAddressValidationError(
              "The address could not be fully verified. Please review the suggested address below."
            );
            setIsSubmitting(false);
            return;
          }

          // Addresses picked from autocomplete were already standardized on
          // pick, so addressChanged is normally false by the time we get here.
          // This is the safety net for addresses typed by hand: adopt a purely
          // cosmetic restyle ("123 William Street" → "123 WILLIAM ST") into
          // both the outgoing payload and the visible fields, but send a real
          // city/state/ZIP correction to the prompt.
          if (addressChanged && !isSafeToAdopt(valResult, addrInput)) {
            setSuggestedAddress(corrected);
            setAddressValidationError(
              "Validation resolved this to a different city, state or ZIP. Please review the suggested address below."
            );
            setIsSubmitting(false);
            return;
          }

          if (addressChanged) {
            const setIfPresent = (key, val) => { if (key in body) body[key] = val; };
            setIfPresent("Address1", corrected.address1);
            setIfPresent("City", corrected.city);
            setIfPresent("State", corrected.state);
            setIfPresent("Zip", corrected.zipCode);
            setIfPresent("ZipCode", corrected.zipCode);

            const paramToValue = {
              Address1: corrected.address1,
              City: corrected.city,
              State: corrected.state,
              Zip: corrected.zipCode,
              ZipCode: corrected.zipCode,
            };
            setFormValues((prev) => {
              const updated = { ...prev };
              fields.forEach((f) => {
                if (f.apiParamName && paramToValue[f.apiParamName] !== undefined) {
                  updated[f.fieldId] = paramToValue[f.apiParamName];
                }
              });
              return updated;
            });
          }
        } else {
          // Try JSON first so we can route ModelState errors to the right
          // fields; fall back to text if the body isn't structured.
          const rawText = await valResponse.text();
          let parsed = null;
          try { parsed = JSON.parse(rawText); } catch { /* not JSON */ }

          const perField = parseFieldErrors(parsed);
          if (Object.keys(perField).length > 0) {
            setFieldErrors(perField);
            const labels = Object.keys(perField)
              .map((k) => {
                const match = fields.find((f) =>
                  (f.apiParamName || "").toLowerCase() === k
                  || (f.apiParamName || "").toLowerCase() + "code" === k
                  || (f.apiParamName || "").toLowerCase() === k.replace(/code$/, "")
                );
                return match?.fieldLabel || k;
              });
            setAddressValidationError(
              `Please correct the highlighted field${labels.length > 1 ? "s" : ""}: ${labels.join(", ")}.`
            );
          } else {
            setAddressValidationError(
              parsed?.title
              || parsed?.message
              || rawText
              || "Unable to validate address. Please check the address fields and try again."
            );
          }
          setIsSubmitting(false);
          return;
        }
      }

      // 1. Save audit trail to local DB. auditOnly tells the API to skip its
      // entity-API forwarding — this form posts the entity API itself below,
      // so forwarding would double-create (and its 401 bounced the session).
      const auditPayload = {
        menuId: parseInt(menuId),
        auditOnly: true,
        submittedBy: null,
        fields: fields.map((f) => ({
          fieldId: f.fieldId,
          fieldName: f.fieldLabel,
          fieldValue:
            f.fieldType === "checkbox"
              ? formValues[f.fieldId] ? "true" : "false"
              : String(formValues[f.fieldId] ?? ""),
        })),
      };

      const submitHeaders = { "Content-Type": "application/json" };
      const submitToken = sessionStorage.getItem("authToken");
      if (submitToken) submitHeaders["Authorization"] = `Bearer ${submitToken}`;

      fetch(`${API_URL}/api/Common/DynamicFormSubmit`, {
        method: "POST",
        headers: submitHeaders,
        body: JSON.stringify(auditPayload),
      }).catch((err) => console.warn("Audit save failed (non-blocking):", err));

      // 2. POST (create) or PATCH (edit) to entity API
      if (!formSubmitApiUrl) {
        throw new Error("No submit API URL configured for this form.");
      }

      // For edit mode, build the update URL by replacing {placeholders} with editData values
      let submitUrl = formSubmitApiUrl;
      let submitMethod = "POST";

      if (isEditMode) {
        submitMethod = "PATCH";
        if (formUpdateApiUrl) {
          // Replace {PropertyName} placeholders with values from editData
          submitUrl = formUpdateApiUrl.replace(/\{(\w+)\}/g, (match, key) => {
            // Try direct match, then case-insensitive match in editData
            if (editData[key] !== undefined) return encodeURIComponent(editData[key]);
            const found = Object.keys(editData).find(
              (k) => k.toLowerCase() === key.toLowerCase()
            );
            return found ? encodeURIComponent(editData[found]) : match;
          });
        }
      }

      const response = await fetch(`${API_URL}${submitUrl}`, {
        method: submitMethod,
        headers: submitHeaders,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        // Read once so we can both surface a readable banner and route
        // ModelState field-level errors back to the matching inputs.
        const rawText = await response.text();
        let parsed = null;
        try { parsed = JSON.parse(rawText); } catch { /* not JSON */ }

        const perField = parseFieldErrors(parsed);
        if (Object.keys(perField).length > 0) setFieldErrors(perField);

        const message =
          (parsed && (parsed.message
            || (parsed.error && (parsed.details ? `${parsed.error}: ${parsed.details}` : parsed.error))
            || parsed.title))
          || rawText
          || `Server error: ${response.status}`;
        throw new Error(message);
      }

      setSubmitStatus("success");

      // After both create and update, return to the search page WITHOUT
      // auto-running a search — instead pass a flash message so the user
      // sees a confirmation banner and can choose what to search next.
      // Avoids an unnecessary load of unfiltered results.
      if (hasSearchPage) {
        const entityLabel = formTitle || "Record";
        const action      = isEditMode ? "updated" : "saved";
        setTimeout(() => {
          navigate(`/${routeSegment}/search`, {
            state: {
              flashMessage: {
                variant: "success",
                text: `${entityLabel} ${action} successfully.`,
              },
            },
            replace: true,
          });
        }, 800);
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      setSubmitErrorMessage(err?.message || String(err));
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render a single field based on its type
  const renderField = (field) => {
    const value = formValues[field.fieldId];
    const isDefaultedFromGrid = !isEditMode && childGridDefaults && field.apiParamName && childGridDefaults[field.apiParamName] !== undefined;
    // Parent-territory lock for menu 2064: non-D99 admins/distributors can
    // only operate within their own territory subtree, so the parent field
    // is forced to their territoryId (set during initial-values load) and
    // disabled here. D99 admins/distributors are unaffected.
    const isLockedParentTerritory =
      menuId === "2064"
      && field.apiParamName === "ParentTerritory_c"
      && isAdminOrDistributor()
      && !isD99AdminOrDistributor();
    // Territory lock for menu 2065: a sub-territory (sub-distributor) admin/
    // distributor creating a rep can only place it in their own territory, so
    // the Territory ID field is forced to their territoryId and disabled.
    // Distributor-level admins/distributors keep an editable (subtree-scoped)
    // autocomplete — only the value is defaulted, not locked.
    const isLockedRepTerritory =
      menuId === "2065"
      && field.apiParamName === "TerritoryID"
      && isSubTerritoryAdminOrDistributor();
    const isDisabled = (isEditMode && field.readOnlyOnEdit) || (!isEditMode && field.readOnlyOnCreate) || isDefaultedFromGrid || isLockedParentTerritory || isLockedRepTerritory || isViewOnlyForRole;
    const placeholderText = field.placeholder
      ? field.placeholder
      : (!isEditMode && field.readOnlyOnCreate)
        ? "Auto"
        : `Enter ${field.fieldLabel}`;

    switch (field.fieldType) {
      case "text":
        return (
          <Form.Control
            type="text"
            value={value || ""}
            onChange={(e) => handleChange(field.fieldId, e.target.value)}
            required={field.isRequired}
            disabled={isDisabled}
            placeholder={placeholderText}
            maxLength={field.maxLength || undefined}
          />
        );

      case "number":
        return (
          <Form.Control
            type="number"
            value={value || ""}
            onChange={(e) => handleChange(field.fieldId, e.target.value)}
            required={field.isRequired}
            disabled={isDisabled}
            placeholder={placeholderText}
          />
        );

      case "date":
        return (
          <Form.Control
            type="date"
            value={value || ""}
            onChange={(e) => handleChange(field.fieldId, e.target.value)}
            required={field.isRequired}
            disabled={isDisabled}
          />
        );

      case "textarea":
        return (
          <Form.Control
            as="textarea"
            rows={3}
            value={value || ""}
            onChange={(e) => handleChange(field.fieldId, e.target.value)}
            required={field.isRequired}
            disabled={isDisabled}
            placeholder={placeholderText}
            maxLength={field.maxLength || undefined}
          />
        );

      case "dropdown": {
        // Menu 2065 Role dropdown — territory-conditional options:
        //   • "Customer Service" only when the selected territory is a
        //     DIRECT CHILD of D99;
        //   • "IT" only when the selected territory IS D99 itself.
        // The currently-selected value always stays in the list so edit mode
        // can render pre-existing reps regardless of territory.
        let dropdownOptions = field.fieldOptions || [];
        if (menuId === "2065" && field.apiParamName === "RoleCode") {
          const terr = String(territoryValue || "").trim().toUpperCase();
          dropdownOptions = dropdownOptions.filter((opt) => {
            if (opt === value) return true;
            const api = dropdownApiValue(field, opt);
            if (api === "CustomerService") return repTerritoryParent === "D99";
            if (api === "IT") return terr === "D99";
            return true;
          });
        }
        return (
          <Form.Select
            value={value || ""}
            onChange={(e) => handleChange(field.fieldId, e.target.value)}
            required={field.isRequired}
            disabled={isDisabled}
          >
            <option value="">-- Select --</option>
            {dropdownOptions.map((opt, idx) => (
              <option key={idx} value={opt}>
                {opt}
              </option>
            ))}
          </Form.Select>
        );
      }

      case "radio":
        return (
          <div>
            {field.fieldOptions &&
              field.fieldOptions.map((opt, idx) => (
                <Form.Check
                  key={idx}
                  inline
                  type="radio"
                  label={opt}
                  name={`field_${field.fieldId}`}
                  value={opt}
                  checked={value === opt}
                  onChange={(e) => handleChange(field.fieldId, e.target.value)}
                  required={field.isRequired}
                  disabled={isDisabled}
                />
              ))}
          </div>
        );

      case "checkbox":
        // Wrap in a control-shaped container so the checkbox visually matches
        // sibling text inputs (same height, same border, same hover affordance)
        // instead of looking like a bare native control floating in the row.
        return (
          <label
            htmlFor={`field-${field.fieldId}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              minHeight: "38px",
              padding: "6px 12px",
              border: "1px solid #ced4da",
              borderRadius: "6px",
              background: isDisabled ? "#e9ecef" : "#ffffff",
              cursor: isDisabled ? "not-allowed" : "pointer",
              fontSize: "14px",
              color: "#212529",
              userSelect: "none",
              margin: 0,
              transition: "border-color 0.15s ease, box-shadow 0.15s ease",
            }}
            onMouseEnter={(e) => {
              if (!isDisabled) e.currentTarget.style.borderColor = "#0052A5";
            }}
            onMouseLeave={(e) => {
              if (!isDisabled) e.currentTarget.style.borderColor = "#ced4da";
            }}
          >
            <input
              id={`field-${field.fieldId}`}
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleChange(field.fieldId, e.target.checked)}
              required={field.isRequired}
              disabled={isDisabled}
              style={{
                margin: 0,
                flexShrink: 0,
                width: "16px",
                height: "16px",
                cursor: isDisabled ? "not-allowed" : "pointer",
                accentColor: "#0052A5",
              }}
            />
            <span style={{ whiteSpace: "nowrap" }}>
              {field.fieldLabel}
              {field.isRequired && <span style={{ color: "#dc3545" }}> *</span>}
            </span>
          </label>
        );

      case "autocomplete": {
        let acConfig = {};
        try {
          // The API splits FieldOptions by comma (for dropdowns), so for autocomplete
          // we may receive a string[] of JSON fragments — rejoin them first
          let raw = field.fieldOptions;
          if (Array.isArray(raw)) raw = raw.join(",");
          acConfig = typeof raw === "string" ? JSON.parse(raw) : (raw || {});
        } catch { /* ignore */ }
        const populate = acConfig.populate || {};

        // ── Cross-field placeholder substitution ──
        // Resolve {ApiParamName} placeholders in defaultParams / queryTemplate
        // against current sibling form values, so an autocomplete can be
        // scoped by another field. Example: a Sales Rep autocomplete with
        //   "defaultParams": "territoryId={TerritoryID}"
        // filters reps to the selected territory. {value} is reserved for the
        // user's typed query and is left untouched.
        const siblingValueByParam = {};
        fields.forEach((sib) => {
          if (sib.apiParamName) {
            siblingValueByParam[sib.apiParamName.toLowerCase()] =
              formValues[sib.fieldId] ?? "";
          }
        });
        const substitutePlaceholders = (str) => {
          if (typeof str !== "string" || !str) return str;
          return str.replace(/\{(\w+)\}/g, (match, key) => {
            if (key === "value") return match;
            const v = siblingValueByParam[key.toLowerCase()];
            return v === undefined ? "" : String(v);
          });
        };
        let resolvedDefaultParams = substitutePlaceholders(acConfig.defaultParams);

        // ── `dependentParams` from FieldOptions JSON ──
        // Mirrors DynamicSearch.mergeDefaultParams: each entry resolves to
        //   "<param>=<currentValueOf(sibling whose ApiParamName === fromField)>"
        // and is appended to defaultParams. Empty sibling values are dropped
        // so the autocomplete behaves as unfiltered until a parent is picked.
        // Shape:
        //   "dependentParams": [
        //     {"param": "territoryId",  "fromField": "territoryID"},
        //     {"param": "salesRepCode", "fromField": "salesRepCode"}
        //   ]
        const deps = Array.isArray(acConfig.dependentParams) ? acConfig.dependentParams : [];
        if (deps.length > 0) {
          const dynPairs = [];
          deps.forEach((d) => {
            if (!d?.param || !d?.fromField) return;
            const v = siblingValueByParam[d.fromField.toLowerCase()];
            if (v === undefined || v === null || v === "" || v === "All") return;
            dynPairs.push(`${encodeURIComponent(d.param)}=${encodeURIComponent(v)}`);
          });
          const dyn = dynPairs.join("&");
          if (dyn) {
            resolvedDefaultParams = resolvedDefaultParams
              ? `${resolvedDefaultParams}&${dyn}`
              : dyn;
          }
        }

        // ── Convention: Sales Rep autocomplete is scoped by sibling Territory ──
        // /api/SalesRep accepts a `territoryId` query param that limits results
        // to reps assigned to that territory (SalesTRP). If the form has a
        // sibling field whose ApiParamName is TerritoryID (or ParentTerritory_c)
        // with a value, auto-inject it so the dropdown is filtered. Skipped
        // when the DB config already supplies a territoryId param.
        if (typeof acConfig.apiUrl === "string"
            && /\/api\/SalesRep\b/i.test(acConfig.apiUrl)
            && !/(^|&)\s*territoryId\s*=/i.test(resolvedDefaultParams || "")) {
          const tVal = siblingValueByParam["territoryid"]
                    ?? siblingValueByParam["parentterritory_c"];
          if (tVal) {
            const extra = `territoryId=${encodeURIComponent(tVal)}`;
            resolvedDefaultParams = resolvedDefaultParams
              ? `${resolvedDefaultParams}&${extra}`
              : extra;
          }
        }

        // Parent-territory rule: D99 admins/distributors picking the parent of
        // a new territory must only see top-level territories (D99 itself +
        // direct children of D99). Hyphenated IDs (e.g. "D57-SD01") are
        // sub-distributors — already nested — and must not appear as a parent
        // option. Sub-distributor users have ParentTerritory_c locked above so
        // the autocomplete never opens for them.
        const isParentTerritoryForD99 =
          menuId === "2064"
          && field.apiParamName === "ParentTerritory_c"
          && isD99AdminOrDistributor();

        // Route through the session reference-data cache when the config
        // targets a cached dataset (Territory / Sales Rep / Customer) with
        // cache-expressible narrowing. withRefDataPreload leaves configs with
        // a clientFilter (the D99 parent-territory rule above) untouched, so
        // those keep the live AutocompleteField, which is the only component
        // that applies clientFilter.
        const resolvedConfig = withRefDataPreload({
          ...acConfig,
          defaultParams: resolvedDefaultParams,
          queryTemplate: substitutePlaceholders(acConfig.queryTemplate),
          ...(isParentTerritoryForD99
            ? { clientFilter: { field: "territoryID", matches: "^[^-]+$" } }
            : {}),
        });

        return (
          <SearchableSelect
            config={resolvedConfig}
            value={value || ""}
            onChange={(val) => handleChange(field.fieldId, val)}
            onSelectItem={(item) => {
              if (Object.keys(populate).length === 0) return;
              setFormValues((prev) => {
                const updated = { ...prev };
                fields.forEach((f) => {
                  if (!f.apiParamName) return;
                  // populate map: { responseKey: targetApiParamName }
                  const entry = Object.entries(populate).find(([, target]) => target === f.apiParamName);
                  if (!entry) return;
                  const respKey = entry[0];
                  // Case-insensitive lookup of the response field
                  let v = item[respKey];
                  if (v === undefined) {
                    const lk = respKey.toLowerCase();
                    const found = Object.keys(item).find((k) => k.toLowerCase() === lk);
                    if (found) v = item[found];
                  }
                  if (v !== undefined && v !== null) updated[f.fieldId] = String(v);
                });
                return updated;
              });
            }}
            disabled={isDisabled}
            placeholder={placeholderText}
            required={field.isRequired}
            maxLength={field.maxLength || undefined}
            // Leave inputWidth unset on form autocompletes so the input keeps
            // its natural 14ch width (uniform across Territory ID, Reports To
            // and friends), and the remainder of the Bootstrap column is the
            // space where the secondary description renders when the user
            // picks a value. Wider cols (Reports To at col-md-6) get more
            // room for longer descriptions — that's intentional, not a gap.
          />
        );
      }

      case "addressAutocomplete": {
        let aaConfig = {};
        try {
          let raw = field.fieldOptions;
          if (Array.isArray(raw)) raw = raw.join(",");
          aaConfig = typeof raw === "string" ? JSON.parse(raw) : (raw || {});
        } catch { /* ignore */ }
        const populate = aaConfig.populate || {};
        return (
          <AddressAutocompleteField
            config={aaConfig}
            value={value || ""}
            onChange={(val) => handleChange(field.fieldId, val)}
            onSelectDetails={async (details) => {
              const missingLabels = [];
              setFormValues((prev) => {
                const updated = { ...prev };
                fields.forEach((f) => {
                  if (!f.apiParamName) return;
                  // Find which detail key maps to this apiParamName
                  const entry = Object.entries(populate).find(([, target]) => target === f.apiParamName);
                  if (!entry) return;
                  const detailKey = entry[0];
                  const v = details?.[detailKey];
                  // Only overwrite when the picked place actually contains a
                  // value. A road-only Google place lacks city/zip; wiping
                  // the existing fields in that case forces the user to
                  // re-type before address validation passes. Preserve what
                  // they had, and surface the gap via a warning instead.
                  if (v !== undefined && v !== null && String(v) !== "") {
                    updated[f.fieldId] = String(v);
                  } else if (!updated[f.fieldId]) {
                    missingLabels.push(f.fieldLabel);
                  }
                });
                return updated;
              });
              if (missingLabels.length > 0) {
                setAddressValidationError(
                  `The picked place doesn't include ${missingLabels.join(", ")}. Please fill in manually.`
                );
                return;
              }
              setAddressValidationError(null);

              // Standardize right here so the user sees the value that will
              // actually be saved — Places hands back "123 William Street",
              // Epicor stores "123 WILLIAM ST". Rewriting it later, at submit,
              // looked like the form was second-guessing a suggestion the user
              // had just picked.
              //
              // Anything not confidently cosmetic is left exactly as Google
              // returned it: the gate on submit still catches it, and
              // interrupting mid-form would be worse friction than the prompt
              // we set out to remove.
              const paramValue = (param) => {
                const entry = Object.entries(populate).find(([, target]) => target === param);
                const dv = entry ? details?.[entry[0]] : undefined;
                if (dv !== undefined && dv !== null && String(dv) !== "") return String(dv);
                const match = fields.find((f) => f.apiParamName === param && formValues[f.fieldId]);
                return match ? String(formValues[match.fieldId]) : "";
              };
              const picked = {
                address1: paramValue("Address1"),
                city: paramValue("City"),
                state: paramValue("State"),
                zipCode: paramValue("Zip") || paramValue("ZipCode"),
              };
              if (!picked.address1) return;

              const result = await validateAddressApi(API_URL, picked);
              if (!isSafeToAdopt(result, picked)) return;

              const c = correctedFrom(result);
              const paramToValue = {
                Address1: c.address1,
                City: c.city,
                State: c.state,
                Zip: c.zipCode,
                ZipCode: c.zipCode,
              };
              setFormValues((prev) => {
                const updated = { ...prev };
                fields.forEach((f) => {
                  if (f.apiParamName && paramToValue[f.apiParamName] !== undefined) {
                    updated[f.fieldId] = paramToValue[f.apiParamName];
                  }
                });
                return updated;
              });
            }}
            disabled={isDisabled}
            placeholder={placeholderText}
            required={field.isRequired}
          />
        );
      }

      default:
        return (
          <Form.Control
            type="text"
            value={value || ""}
            onChange={(e) => handleChange(field.fieldId, e.target.value)}
            required={field.isRequired}
            disabled={isDisabled}
          />
        );
    }
  };

  // Module-level entitlement (Menu.RequiredActionId via the shared menu tree):
  // a role without the module's search/view action can't open its form by URL.
  // (Placed after all hooks — React needs a stable hook order.)
  const menuEntryForAccess = menuData.find((m) => String(m.menuId) === String(menuId));
  if (menuEntryForAccess && !canAccessMenu(menuEntryForAccess)) {
    return (
      <div className="qw-root content-halo content-halo-narrow" style={{ maxWidth: 520, margin: "120px auto", padding: "36px 32px", borderRadius: 16, textAlign: "center" }}>
        <h4 style={{ color: "#0F172A", fontWeight: 700 }}>No access to this page</h4>
        <p style={{ color: "#64748B", fontSize: 14 }}>Your role is not entitled to this module. Changes to role access apply at your next login.</p>
      </div>
    );
  }

  return (
    <div className="qw-root content-halo dyn-qw dyn-form-shell dyn-form-2x" style={{ padding: "24px 26px 32px", borderRadius: 16 }}>
      <div className="dyn-form-scroll">
      <Container fluid className="px-0">
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" /> Loading form...
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : fields.length === 0 ? (
          <Alert variant="info">No form configured for this menu.</Alert>
        ) : (
          <>
            {submitStatus === "success" && (
              <Alert
                variant="success"
                dismissible
                onClose={() => setSubmitStatus(null)}
              >
                {isEditMode ? "Record updated successfully! Returning to search..." : "Record created successfully! Returning to search..."}
              </Alert>
            )}
            {submitStatus === "error" && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => { setSubmitStatus(null); setSubmitErrorMessage(null); }}
              >
                <strong>{isEditMode ? "Failed to update record." : "Failed to submit form."}</strong>
                {submitErrorMessage && (
                  <div style={{ marginTop: 4, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {submitErrorMessage}
                  </div>
                )}
              </Alert>
            )}
            {addressValidationError && (
              <Alert
                variant="warning"
                dismissible
                onClose={() => { setAddressValidationError(null); setSuggestedAddress(null); }}
              >
                <strong>Address Validation:</strong> {addressValidationError}
                {suggestedAddress && (
                  <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                    <span>
                      <strong>Suggested Address:</strong>{" "}
                      {[suggestedAddress.address1, suggestedAddress.city, suggestedAddress.state, suggestedAddress.zipCode]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={applySuggestedAddress}
                    >
                      Use Suggested Address
                    </Button>
                  </div>
                )}
              </Alert>
            )}

            {/* ── Form header ── */}
            <h5 style={{ color: "#0052A5", fontWeight: 600, marginBottom: "16px" }}>
              {isViewOnlyForRole
                ? `View ${formTitle || "Entry"}`
                : isEditMode
                  ? `Edit ${formTitle || "Entry"}`
                  : (formTitle || "Add New Entry")}
            </h5>

            {isViewOnlyForRole && (
              <Alert variant="info" style={{ marginBottom: "16px" }}>
                You have view-only access to these details.
              </Alert>
            )}

            <Form noValidate validated={validated} onSubmit={handleSubmit}>
              {(() => {
                // Group fields by Section, preserving displayOrder within each
                // group and section order by first appearance.
                const sectionMap = new Map();
                fields.forEach((f) => {
                  const key = f.section || "Details";
                  if (!sectionMap.has(key)) sectionMap.set(key, []);
                  sectionMap.get(key).push(f);
                });
                return [...sectionMap.entries()].map(([sectionName, sectionFields]) => (
                  <div
                    key={sectionName}
                    style={{
                      background: "#ffffff",
                      border: "1px solid #dee2e6",
                      borderRadius: "8px",
                      padding: "18px 22px 8px",
                      marginBottom: "18px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                    }}
                  >
                    <div
                      style={{
                        borderLeft: "3px solid #0052A5",
                        paddingLeft: "10px",
                        marginBottom: "16px",
                      }}
                    >
                      <div style={{ color: "#0052A5", fontWeight: 600, fontSize: "14px", letterSpacing: "0.3px" }}>
                        {sectionName.toUpperCase()}
                      </div>
                    </div>
                    <Row>
                      {sectionFields.map((field) => {
                        // Width policy:
                        //   • textarea — always the full row (12).
                        //   • date / number / dropdown — bounded-length
                        //     values (a date string is ~10 chars, a percent
                        //     is 1–3 digits, a status is one word). Cap at
                        //     3 grid columns even if the DB says wider,
                        //     so the picker isn't a 1/3-row-wide eyesore.
                        //   • checkbox — at least 3 so multi-word labels
                        //     ("Primary Sales Rep") aren't cropped.
                        //   • everything else — trust the DB-provided
                        //     ColumnWidth (default 6 = half a row).
                        const dbWidth = field.columnWidth || 6;
                        let baseWidth;
                        if (field.fieldType === "textarea") {
                          baseWidth = 12;
                        } else if (
                          field.fieldType === "date" ||
                          field.fieldType === "number" ||
                          field.fieldType === "dropdown"
                        ) {
                          baseWidth = Math.min(dbWidth, 3);
                        } else {
                          baseWidth = dbWidth;
                        }
                        const colWidth =
                          field.fieldType === "checkbox"
                            ? Math.max(baseWidth, 2)
                            : baseWidth;
                        return (
                        <Col
                          key={field.fieldId}
                          md={colWidth}
                          className="mb-3"
                        >
                          <Form.Group>
                            {/* Every label is forced onto a single line with
                                a fixed height so controls underneath start at
                                the exact same Y across a row, regardless of
                                column width. Without this, "Primary Sales
                                Rep" at md=2 wraps to two lines while "Name"
                                at md=3 stays on one — pushing the inputs out
                                of vertical alignment. Long labels truncate
                                with ellipsis; full text stays in the title
                                tooltip. The checkbox row uses an invisible
                                spacer of the same height so it lines up too. */}
                            {(() => {
                              const labelStyle = {
                                fontSize: "13px",
                                fontWeight: 500,
                                marginBottom: "4px",
                                lineHeight: "1.4",
                                height: "20px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "block",
                              };
                              if (field.fieldType === "checkbox") {
                                return (
                                  <Form.Label
                                    aria-hidden="true"
                                    style={{ ...labelStyle, visibility: "hidden" }}
                                  >
                                    &nbsp;
                                  </Form.Label>
                                );
                              }
                              return (
                                <Form.Label style={labelStyle} title={field.fieldLabel}>
                                  {field.fieldLabel}
                                  {field.isRequired && (
                                    <span style={{ color: "#dc3545" }}> *</span>
                                  )}
                                </Form.Label>
                              );
                            })()}
                            {renderField(field)}
                            {field.maxLength && (field.fieldType === "text" || field.fieldType === "textarea") && (() => {
                              const used = String(formValues[field.fieldId] ?? "").length;
                              const max  = field.maxLength;
                              const ratio = used / max;
                              const color = ratio >= 1 ? "#dc3545" : ratio >= 0.9 ? "#d97706" : "#6c757d";
                              return (
                                <div style={{ fontSize: "11px", color, marginTop: "2px", textAlign: "right" }}>
                                  {used} / {max}
                                </div>
                              );
                            })()}
                            <Form.Control.Feedback type="invalid">
                              {field.fieldLabel} is required.
                            </Form.Control.Feedback>
                            {(() => {
                              const serverErr = fieldErrorFor(field.apiParamName, fieldErrors);
                              if (!serverErr) return null;
                              return (
                                <div
                                  style={{
                                    color: "#dc3545",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    marginTop: "4px",
                                  }}
                                >
                                  {serverErr}
                                </div>
                              );
                            })()}
                          </Form.Group>
                        </Col>
                        );
                      })}
                    </Row>
                  </div>
                ));
              })()}

                {/* ── Child Grids (edit mode only) ── */}
                {isEditMode && childGrids.length > 0 && (
                  <div style={{ marginTop: "20px" }}>
                    {childGrids.map((grid) => {
                      const gridState = childGridData[grid.childGridId] || { data: [], loading: true, error: null };
                      const columnTree = buildColumnTree(grid.columns || []);

                      return (
                        <div
                          key={grid.childGridId}
                          style={{
                            marginBottom: "16px",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              background: "#e9ecef",
                              padding: "8px 14px 8px 8px",
                              fontWeight: 600,
                              fontSize: "13px",
                              color: "#0052A5",
                              borderBottom: collapsedGrids[grid.childGridId] ? "none" : "1px solid #dee2e6",
                              cursor: "pointer",
                              userSelect: "none",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                            onClick={() =>
                              setCollapsedGrids((prev) => ({
                                ...prev,
                                [grid.childGridId]: !prev[grid.childGridId],
                              }))
                            }
                          >
                            <span style={{ display: "flex", alignItems: "center", marginLeft: 0 }}>
                              <span
                                style={{
                                  fontSize: "14px",
                                  marginRight: "6px",
                                  marginLeft: 0,
                                  color: "#0052A5",
                                }}
                              >
                                {collapsedGrids[grid.childGridId] ? "▶" : "▼"}
                              </span>
                              {grid.gridTitle}
                              {!gridState.loading && (() => {
                                // Show full total when the backend reported one
                                // (paginated grids); otherwise fall back to the
                                // rendered length (nested-array grids).
                                const headerCount = gridState.totalCount ?? gridState.data.length;
                                return (
                                  <span style={{ fontWeight: 400, color: "#666", marginLeft: "8px", fontSize: "12px" }}>
                                    ({headerCount} record{headerCount !== 1 ? "s" : ""})
                                  </span>
                                );
                              })()}
                            </span>
                            {grid.showAddButton && evaluateCondition(grid.addButtonCondition, editData) && canAddToChildGrid(grid) && (
                              <Button
                                variant="success"
                                size="sm"
                                style={{ fontSize: "11px", padding: "2px 10px", fontWeight: 600 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Parse AddDefaultParams JSON and resolve {placeholders} from editData
                                  // e.g. {"ParentTerritory_c": "{TerritoryID}"} → { ParentTerritory_c: "D20" }
                                  const defaults = {};
                                  try {
                                    const paramMap = JSON.parse(grid.addDefaultParams || "{}");
                                    Object.entries(paramMap).forEach(([paramName, paramVal]) => {
                                      const match = String(paramVal).match(/^\{(\w+)\}$/);
                                      if (match) {
                                        const editKey = match[1];
                                        const found = Object.keys(editData).find(
                                          (k) => k.toLowerCase() === editKey.toLowerCase()
                                        );
                                        if (found) defaults[paramName] = editData[found];
                                      } else {
                                        defaults[paramName] = paramVal; // static value
                                      }
                                    });
                                  } catch { /* ignore parse errors */ }
                                  const targetMenuId = grid.addMenuId || menuId;
                                  // Prefer the target menu's slug path so the URL
                                  // stays human-readable; fall back to numeric if
                                  // the menu tree hasn't been slug-backfilled yet.
                                  // "Add" from a child grid is always a create.
                                  const targetSegment =
                                    getLeafSlug(menuData, targetMenuId) || targetMenuId;
                                  navigate(`/${targetSegment}/create`, {
                                    state: {
                                      childGridDefaults: defaults,
                                      returnTo: {
                                        pathname: location.pathname,
                                        state: location.state || null,
                                      },
                                    },
                                  });
                                }}
                              >
                                {grid.addButtonLabel || "+ Add"}
                              </Button>
                            )}
                          </div>

                          {!collapsedGrids[grid.childGridId] && (
                            <div style={{ padding: "0" }}>
                              {gridState.loading ? (
                                <div style={{ padding: "16px", textAlign: "center", fontSize: "13px", color: "#888" }}>
                                  <Spinner animation="border" size="sm" /> Loading {grid.gridTitle.toLowerCase()}...
                                </div>
                              ) : gridState.error ? (
                                <div style={{ padding: "12px", color: "#dc3545", fontSize: "13px" }}>
                                  Failed to load: {gridState.error}
                                </div>
                              ) : (
                                <NestedDataGrid
                                  columns={columnTree}
                                  data={gridState.data}
                                  compact
                                  readOnly={childGridReadOnly}
                                  onError={(msg) => {
                                    setSubmitErrorMessage(msg);
                                    setSubmitStatus("error");
                                  }}
                                  onDataChange={(row, field, newValue) => {
                                    // Update the local grid data so UI reflects the change
                                    setChildGridData((prev) => {
                                      const gridId = grid.childGridId;
                                      const currentData = prev[gridId]?.data || [];
                                      const updatedData = currentData.map((r) =>
                                        r === row ? { ...r, [field]: newValue } : r
                                      );
                                      return {
                                        ...prev,
                                        [gridId]: { ...prev[gridId], data: updatedData },
                                      };
                                    });
                                  }}
                                  onRowClick={grid.showAddButton && canEditChildGridRows(grid) ? (row) => {
                                    // Navigate to edit form with clicked row's data
                                    const targetMenuId = grid.addMenuId || menuId;
                                    const targetSegment =
                                      getLeafSlug(menuData, targetMenuId) || targetMenuId;
                                    navigate(`/${targetSegment}/edit`, {
                                      state: {
                                        editData: row,
                                        returnTo: {
                                          pathname: location.pathname,
                                          state: location.state || null,
                                        },
                                      },
                                    });
                                  } : undefined}
                                />
                              )}

                              {/* ── Server-driven pagination bar ────────────
                                  Only renders when the backend reported a
                                  totalCount that exceeds the current page —
                                  i.e. there's at least one more page worth
                                  of records. Hides automatically for grids
                                  whose dataPath dives into a nested array
                                  (totalCount falls back to data.length and
                                  the condition is never true). */}
                              {(() => {
                                const total = gridState.totalCount ?? 0;
                                const pageSize = gridState.pageSize ?? CHILD_GRID_PAGE_SIZE;
                                const page = gridState.page ?? 0;
                                if (gridState.loading || total <= pageSize) return null;

                                const totalPages = Math.max(1, Math.ceil(total / pageSize));
                                const showFrom = page * pageSize + 1;
                                const showTo = Math.min(showFrom + (gridState.data?.length || 0) - 1, total);

                                return (
                                  <div className="d-flex justify-content-between align-items-center"
                                    style={{
                                      padding: "8px 12px",
                                      borderTop: "1px solid #dee2e6",
                                      fontSize: "12px",
                                      background: "#fafbfc",
                                    }}>
                                    <span style={{ color: "#555" }}>
                                      Showing {showFrom}&ndash;{showTo} of {total} records
                                    </span>
                                    <div className="d-flex align-items-center gap-2">
                                      <span style={{ color: "#555" }}>
                                        Page {page + 1} of {totalPages}
                                      </span>
                                      <Button size="sm" variant="outline-primary"
                                        disabled={page === 0}
                                        onClick={() => fetchChildGridPage(grid, page - 1)}>
                                        &lsaquo; Prev
                                      </Button>
                                      <Button size="sm" variant="outline-primary"
                                        disabled={page >= totalPages - 1}
                                        onClick={() => fetchChildGridPage(grid, page + 1)}>
                                        Next &rsaquo;
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="mt-3 d-flex gap-2">
                  {!isViewOnlyForRole && (
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner animation="border" size="sm" /> {isEditMode ? "Updating..." : "Saving..."}
                      </>
                    ) : (
                      isEditMode ? "Update" : "Save"
                    )}
                  </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline-secondary"
                    disabled={isSubmitting}
                    onClick={() => {
                      const resetValues = {};
                      fields.forEach((f) => {
                        resetValues[f.fieldId] = f.fieldType === "checkbox" ? false : "";
                      });
                      setFormValues(resetValues);
                      setValidated(false);
                      setSubmitStatus(null);
                      setSubmitErrorMessage(null);
                      setFieldErrors({});
                    }}
                  >
                    Reset
                  </Button>
                  {(hasSearchPage || returnTo) && (
                    <Button
                      type="button"
                      variant="outline-primary"
                      onClick={() => {
                        if (returnTo && typeof returnTo === "object" && returnTo.pathname) {
                          navigate(returnTo.pathname, { state: returnTo.state ?? null });
                        } else if (typeof returnTo === "string" && returnTo) {
                          navigate(returnTo);
                        } else {
                          navigate(`/${routeSegment}/search`);
                        }
                      }}
                    >
                      {returnTo && typeof returnTo === "object" &&
                        (/\/(create|edit)(\/|$)/.test(returnTo.pathname || ''))
                        ? "Back"
                        : "Return to Search"}
                    </Button>
                  )}
                </div>
              </Form>
          </>
        )}
      </Container>
      </div>
    </div>
  );
}

export default DynamicForm;
